import React, { useState, useRef } from 'react';
import {
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Select,
  Input,
  Row,
  Col,
  message,
  Tooltip,
} from 'antd';
import { useParams, useHistory } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  ProfileOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  BarcodeOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
  QrcodeOutlined,
} from '@ant-design/icons';
import { QRCodeCanvas } from 'qrcode.react';

import MyTable from '../helpers/MyTable';
import SubPageLayout from '../helpers/SubPageLayout';
import SubPageHeader from '../helpers/SubPageHeader';

const { Option } = Select;

// Static demo data: products inside each project
const initialProjectProductsByCode = {
  'PRJ-001': [
    {
      id: 1,
      productCode: 'MineSAFE_Modular_Design',
      productName: 'MineSAFE Modular Design',
      serialNumber: 'MAF1236',
      status: 'In Progress',
      createdBy: 'Jean-Beni',
      image: '/product_images/MineSAFE_Modular_Design.png',
      preBuildComplete: true,
      buildSheetComplete: false,
      drawingsComplete: false,
      stickersComplete: false,
      documentsComplete: false,
      fabricator: 'MineARC Fabrication',
      paintBlast: 'SurfaceCoat Solutions',
    },
    {
      id: 2,
      productCode: 'ChemSAFE_Standard_Design',
      productName: 'ChemSAFE Standard Design',
      serialNumber: 'MAF1237',
      status: 'Planned',
      createdBy: 'Sarah Johnson',
      image: '/product_images/ChemSAFE_Standard_Design.png',
      preBuildComplete: false,
      buildSheetComplete: false,
      drawingsComplete: false,
      stickersComplete: false,
      documentsComplete: false,
      fabricator: 'SteelWorks Pty Ltd',
      paintBlast: 'PrimeBlast Services',
    },
  ],
  'PRJ-002': [
    {
      id: 3,
      productCode: 'TunnelSAFE_Standard_Design',
      productName: 'TunnelSAFE Standard Design',
      serialNumber: 'MAF2231',
      status: 'In Progress',
      createdBy: 'Admin',
      image: '/product_images/TunnelSAFE_Standard_Design.png',
      preBuildComplete: true,
      buildSheetComplete: true,
      drawingsComplete: false,
      stickersComplete: false,
      documentsComplete: false,
      fabricator: 'MineARC Fabrication',
      paintBlast: 'SurfaceCoat Solutions',
    },
  ],
  'PRJ-003': [
    {
      id: 4,
      productCode: 'CoalSAFE_MSHA_MS',
      productName: 'CoalSAFE MSHA MS',
      serialNumber: 'MAF3312',
      status: 'Completed',
      createdBy: 'Jean-Beni',
      image: '/product_images/CoalSAFE_MSHA_MS.png',
      preBuildComplete: true,
      buildSheetComplete: true,
      drawingsComplete: true,
      stickersComplete: true,
      documentsComplete: true,
      fabricator: 'Global Steel Solutions',
      paintBlast: 'Premium Blast & Paint',
    },
  ],
  'PRJ-004': [
    {
      id: 5,
      productCode: 'ChemSAFE_Sealed_Room',
      productName: 'ChemSAFE Sealed Room',
      serialNumber: 'MAF4410',
      status: 'In Progress',
      createdBy: 'System',
      image: '/product_images/ChemSAFE_Sealed_Room.png',
      preBuildComplete: true,
      buildSheetComplete: true,
      drawingsComplete: true,
      stickersComplete: false,
      documentsComplete: false,
      fabricator: 'SafeBuild Contractors',
      paintBlast: 'PrimeBlast Services',
    },
  ],
  'PRJ-005': [
    {
      id: 6,
      productCode: 'StormSAFE',
      productName: 'StormSAFE Shelter',
      serialNumber: 'MAF5515',
      status: 'Planned',
      createdBy: 'Jean-Beni',
      image: '/product_images/StormSAFE.png',
      preBuildComplete: false,
      buildSheetComplete: false,
      drawingsComplete: false,
      stickersComplete: false,
      documentsComplete: false,
      fabricator: 'StormFab Engineering',
      paintBlast: 'SurfaceCoat Solutions',
    },
  ],
};

// Product catalog for dropdown + images
const productCatalog = [
  {
    code: 'MineSAFE_Modular_Design',
    name: 'MineSAFE Modular Design',
    image: '/product_images/MineSAFE_Modular_Design.png',
  },
  {
    code: 'ChemSAFE_Standard_Design',
    name: 'ChemSAFE Standard Design',
    image: '/product_images/ChemSAFE_Standard_Design.png',
  },
  {
    code: 'TunnelSAFE_Standard_Design',
    name: 'TunnelSAFE Standard Design',
    image: '/product_images/TunnelSAFE_Standard_Design.png',
  },
  {
    code: 'CoalSAFE_MSHA_MS',
    name: 'CoalSAFE MSHA MS',
    image: '/product_images/CoalSAFE_MSHA_MS.png',
  },
  {
    code: 'StormSAFE',
    name: 'StormSAFE Shelter',
    image: '/product_images/StormSAFE.png',
  },
];

const ProjectProducts = () => {
  const { code: projectCode } = useParams(); // from /projects/:code/products
  const history = useHistory();
  const [form] = Form.useForm();

  const [projectProductsByCode, setProjectProductsByCode] = useState(
    initialProjectProductsByCode
  );
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // QR modal state
  const [isQrModalVisible, setIsQrModalVisible] = useState(false);
  const [qrProduct, setQrProduct] = useState(null);
  const qrCanvasRef = useRef(null);

  const products = projectProductsByCode[projectCode] || [];

  // ---------- NAVIGATION ----------

  const handlePreBuildClick = (product) => {
    history.push('/pre-build', {
      product,
      projectCode,
    });
  };

  // ---------- QR HELPERS ----------

  const openQrModal = (product) => {
    setQrProduct(product);
    setIsQrModalVisible(true);
  };

  const closeQrModal = () => {
    setIsQrModalVisible(false);
    setQrProduct(null);
  };

  const handlePrintQr = () => {
    if (!qrCanvasRef.current) return;
    const canvas = qrCanvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(
      `
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
            }
            img {
              width: 260px;
              height: 260px;
            }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" />
        </body>
      </html>
      `
    );
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // ---------- TABLE COLUMNS ----------

  const columns = [
    {
      title: '',
      dataIndex: 'image',
      key: 'image',
      width: 90,
      render: (src, record) => (
        <div
          style={{
            width: 100,
            height: 100,
            borderRadius: 8,
            overflow: 'hidden',
            background: '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={src}
            alt={record.productName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'productCode',
      key: 'productCode',
    },
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: 'Serial',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
    },
    {
      title: 'QR Code',
      key: 'qr',
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Open QR code">
          <div
            style={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={() => openQrModal(record)}
          >
            <QRCodeCanvas value={record.serialNumber} size={48} />
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = 'default';
        if (status === 'Completed') color = 'green';
        else if (status === 'In Progress') color = 'blue';
        else if (status === 'Planned') color = 'gold';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isInProgress = record.status === 'In Progress';

        const btnStyle = {
          height: 26,
          fontSize: 11,
          padding: '0 8px',
          borderRadius: 999,
        };

        return (
          <Space size="small" wrap>
            <Button
              size="small"
              style={btnStyle}
              icon={<ProfileOutlined />}
              onClick={() => handlePreBuildClick(record)}
            >
              Pre-build
            </Button>

            <Button
              size="small"
              disabled={!record.preBuildComplete}
              style={btnStyle}
              icon={<FileTextOutlined />}
            >
              Build Sheet
            </Button>

            <Button
              size="small"
              disabled={!record.buildSheetComplete}
              style={btnStyle}
              icon={<FileSearchOutlined />}
            >
              Drawings
            </Button>

            <Button
              size="small"
              disabled={!record.drawingsComplete}
              style={btnStyle}
              icon={<BarcodeOutlined />}
            >
              Stickers
            </Button>

            <Button
              size="small"
              disabled={!record.stickersComplete}
              style={btnStyle}
              icon={<FolderOpenOutlined />}
            >
              Documents
            </Button>

            {!isInProgress && (
              <Button
                size="small"
                danger
                style={btnStyle}
                icon={<DeleteOutlined />}
              >
                Remove
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  // ---------- ADD PRODUCT MODAL LOGIC ----------

  const openAddProductModal = () => {
    // compute next available serial for this project
    const serialNumbers = (products || [])
      .map((p) => p.serialNumber)
      .filter((s) => /^MAF\d+$/.test(s))
      .map((s) => parseInt(s.slice(3), 10));

    const maxSerial = serialNumbers.length ? Math.max(...serialNumbers) : 1000;
    const nextSerial = `MAF${maxSerial + 1}`;

    form.setFieldsValue({
      productCode: undefined,
      serialNumber: nextSerial,
    });
    setSelectedProduct(null);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedProduct(null);
  };

  const handleAddProduct = (values) => {
    const catalogEntry = productCatalog.find(
      (p) => p.code === values.productCode
    );

    const newProduct = {
      id: Date.now(),
      productCode: values.productCode,
      productName: catalogEntry ? catalogEntry.name : values.productCode,
      serialNumber: values.serialNumber,
      status: 'Planned',
      createdBy: 'Current User',
      image: catalogEntry ? catalogEntry.image : '',
      preBuildComplete: false,
      buildSheetComplete: false,
      drawingsComplete: false,
      stickersComplete: false,
      documentsComplete: false,
    };

    setProjectProductsByCode((prev) => ({
      ...prev,
      [projectCode]: [newProduct, ...(prev[projectCode] || [])],
    }));

    message.success('Product added to project');
    handleModalCancel();
  };

  const handleProductChange = (value) => {
    const catalogEntry = productCatalog.find((p) => p.code === value);
    setSelectedProduct(catalogEntry || null);
  };

  return (
    <SubPageLayout
      title={`Order Products - ${projectCode || ''}`}
      extra={
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push('/projects')}
          >
            Back to Orders
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAddProductModal}
          >
            Add Product to Order
          </Button>
        </Space>
      }
    >
      <SubPageHeader
        searchLoading={false}
        searchPlaceholder="Search products in this order"
      />

      <MyTable columns={columns} dataSource={products} rowKey="id" />

      {/* 🌟 Add Product to Project Modal */}
      <Modal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        footer={null}
        centered
        width={940}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background:
              'linear-gradient(135deg, #ffffff 0%, #f3f4ff 40%, #eef7ff 100%)',
            boxShadow: '0 18px 45px rgba(15, 23, 42, 0.18)',
          }}
        >
          {/* Header */}
          <div
            style={{
              marginBottom: 18,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: '#111827',
                  marginBottom: 4,
                }}
              >
                Add Product to Order
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Select a product and assign the next available serial number.
              </div>
            </div>
            {projectCode && (
              <div
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: '#e0ecff',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#1d4ed8',
                  whiteSpace: 'nowrap',
                }}
              >
                Project: {projectCode}
              </div>
            )}
          </div>

          <Row gutter={24}>
            {/* Left: form fields */}
            <Col span={14}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleAddProduct}
                requiredMark="optional"
              >
                <Form.Item
                  label="Product Name"
                  name="productCode"
                  rules={[
                    { required: true, message: 'Please select a product' },
                  ]}
                >
                  <Select
                    placeholder="Select product"
                    showSearch
                    optionFilterProp="children"
                    onChange={handleProductChange}
                    filterOption={(input, option) =>
                      (option?.children || '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {productCatalog.map((p) => (
                      <Option key={p.code} value={p.code}>
                        {p.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Next Available Serial"
                  name="serialNumber"
                  rules={[
                    { required: true, message: 'Please enter a serial number' },
                    {
                      pattern: /^MAF\d+$/i,
                      message: 'Serial must start with MAF, e.g. MAF1238',
                    },
                  ]}
                >
                  <Input placeholder="MAF1238" />
                </Form.Item>

                <div
                  style={{
                    marginTop: 16,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                  }}
                >
                  <Button onClick={handleModalCancel}>Cancel</Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => form.submit()}
                  >
                    Add Product
                  </Button>
                </div>
              </Form>
            </Col>

            {/* Right: product image preview */}
            <Col span={10}>
              <div
                style={{
                  height: 360,
                  width: 360,
                  borderRadius: 16,
                  background: '#f9fafb',
                  border: '1px dashed #d1d5db',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  textAlign: 'center',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                {selectedProduct ? (
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        marginBottom: 8,
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#111827',
                      }}
                    >
                      {selectedProduct.name}
                    </div>
                    <div
                      style={{
                        width: '100%',
                        height: 310,
                        borderRadius: 12,
                        overflow: 'hidden',
                        background: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.name}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#9ca3af', fontSize: 13 }}>
                    Select a product to preview its image here.
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Modal>

      {/* 🔍 QR CODE MODAL */}
      <Modal
        visible={isQrModalVisible}
        onCancel={closeQrModal}
        footer={null}
        centered
        width={400}
        destroyOnClose
        title={null}
      >
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background: '#ffffff',
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
            textAlign: 'center',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <QrcodeOutlined
              style={{ fontSize: 24, color: '#111827', marginBottom: 4 }}
            />
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: '#111827',
                marginBottom: 4,
              }}
            >
              Product QR Code
            </div>
            {qrProduct && (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {qrProduct.productName} &mdash; {qrProduct.serialNumber}
              </div>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 12,
            }}
          >
            {qrProduct && (
              <QRCodeCanvas
                value={qrProduct.serialNumber}
                size={240}
                ref={qrCanvasRef}
              />
            )}
          </div>

          <Space style={{ marginTop: 8 }}>
            <Button onClick={closeQrModal}>Close</Button>
            <Button type="primary" icon={<QrcodeOutlined />} onClick={handlePrintQr}>
              Print QR
            </Button>
          </Space>
        </div>
      </Modal>
    </SubPageLayout>
  );
};

export default ProjectProducts;
